import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import { DarkModeProvider } from '@/context/DarkModeContext';

// Initialize i18n
import '@/i18n';
import { useTranslation } from 'react-i18next';

// Lazy load pages for better performance
import AuthCallback from '@/pages/AuthCallback';
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const DonatePage = lazy(() => import('@/pages/DonatePage'));
const DonationSuccess = lazy(() => import('@/pages/DonationSuccess'));
const ImpactPage = lazy(() => import('@/pages/ImpactPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const Dashboard = lazy(() => import('@/pages/DashboardNew'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const RecurringDonationsPage = lazy(() => import('@/pages/RecurringDonationsPage'));
const PaymentInstructions = lazy(() => import('@/pages/PaymentInstructions'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboardNew'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUsePage'));
const FeesPage = lazy(() => import('@/pages/FeesPage'));
const HelpCenterPage = lazy(() => import('@/pages/HelpCenterPage'));
const DonationLinkPage = lazy(() => import('@/pages/DonationLinkPage'));
const EmbedDonate = lazy(() => import('@/pages/EmbedDonate'));
const EmbedDocs = lazy(() => import('@/pages/EmbedDocs'));

import { Toaster } from '@/components/ui/sonner';
import InstallPrompt from '@/components/InstallPrompt';
import ErrorBoundary from '@/components/ErrorBoundary';
import CookieConsent from '@/components/CookieConsent';
import { Loader2 } from 'lucide-react';

// Loading component with i18n support
const LoadingFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600 dark:text-purple-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-300 text-lg">{t('common.loading')}</p>
      </div>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js', { updateViaCache: 'none' })
          .then((registration) => {
            registration.update();
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
        const hadController = Boolean(navigator.serviceWorker.controller);
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!hadController || refreshing) return;
          refreshing = true;
          window.location.reload();
        });
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <div className="App">
          <Toaster position="top-right" />
          <InstallPrompt />
          <CookieConsent />
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <AppRoutes />
            </Suspense>
          </BrowserRouter>
        </div>
      </DarkModeProvider>
    </ErrorBoundary>
  );
}

// Detect the OAuth fragment synchronously during render, before any protected page runs
function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/donate" element={<DonatePage />} />
                <Route path="/donation-success" element={<DonationSuccess />} />
                <Route path="/impact" element={<ImpactPage />} />
                <Route path="/payment-instructions" element={<PaymentInstructions />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/recurring-donations" element={<RecurringDonationsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                <Route path="/fees" element={<FeesPage />} />
                <Route path="/help" element={<HelpCenterPage />} />
                <Route path="/give/:donationLink" element={<DonationLinkPage />} />
                <Route path="/embed/donate" element={<EmbedDonate />} />
                <Route path="/embed/docs" element={<EmbedDocs />} />
              </Routes>
  );
}

export default App;